import type { LineChartProps, RenderTooltipContentData } from '@shopify/polaris-viz';
import type { IReportTable } from '../../common/Visualization/types';
import type { IRMetricKey } from '../../common/type';

import { getFormattedByInterval, useMetricsTimeFilter } from '~/modules/analytics';
import { PageTooltip, VersionTooltip } from '../components/common';
import { REPORT_METRIC_INFO } from '../../common';
import { useChartData } from './useChartData';
import { useMemo, useCallback } from 'react';
import { getBarChartData } from '../utils';

export interface LineChartDataProps {
  metricKey: IRMetricKey;
  data: IReportTable;
  dimensions: string[];
  hasFilterByCampaign: boolean;
}

export const useBarChartData = ({
  metricKey,
  data,
  dimensions,
  hasFilterByCampaign,
}: LineChartDataProps) => {
  const { currentDates, previousDates } = useMetricsTimeFilter();

  const {
    seriesNameFormatter,
    formatKeyByInterval,
    yAxisOptions,
    interval,
    formatValue,
    templateLabelById,
    onlyHasTimeDimension,
  } = useChartData({
    dimensions,
    data,
    metricKey,
  });

  const metricName = useMemo(() => {
    return REPORT_METRIC_INFO[metricKey].title;
  }, [metricKey]);

  const { seriesData, seriesKey, isFirstTimeDimension } = useMemo(() => {
    return getBarChartData({
      data,
      metricKey,
      dimensions,
      dateFilter: { currentDates, previousDates },
      templateLabelById,
      hasFilterByCampaign,
    });
  }, [
    metricKey,
    data,
    dimensions,
    currentDates,
    previousDates,
    templateLabelById,
    hasFilterByCampaign,
  ]);

  const renderTooltipContent = useCallback(
    (data: RenderTooltipContentData) => {
      if (dimensions.length <= 1)
        return (
          <PageTooltip
            data={data}
            formatValue={formatValue}
            formatKeyByInterval={onlyHasTimeDimension ? formatKeyByInterval : undefined}
            metricKey={metricKey}
          />
        );
      return (
        <VersionTooltip
          data={data}
          seriesKey={seriesKey}
          formatValue={formatValue}
          seriesData={seriesData}
          metricKey={metricKey}
        />
      );
    },
    [
      seriesKey,
      dimensions,
      formatValue,
      formatKeyByInterval,
      onlyHasTimeDimension,
      seriesData,
      metricKey,
    ],
  );

  const xAxisOptions: LineChartProps['xAxisOptions'] = useMemo(() => {
    return {
      labelFormatter: (value) => {
        if (!dimensions.length) return metricName;
        if (isFirstTimeDimension) return getFormattedByInterval(value, interval);

        return `${value}`;
      },
    };
  }, [interval, dimensions, metricName, isFirstTimeDimension]);

  const tooltipOptions: LineChartProps['tooltipOptions'] = useMemo(() => {
    return {
      titleFormatter: (value) => {
        if (!dimensions.length || onlyHasTimeDimension) return metricName;

        if (isFirstTimeDimension) {
          return getFormattedByInterval(value, interval, {
            isExpandDetail: true,
          });
        }

        return `${value}`;
      },
      valueFormatter: formatValue,
      renderTooltipContent,
    };
  }, [
    interval,
    formatValue,
    renderTooltipContent,
    metricName,
    dimensions,
    isFirstTimeDimension,
    onlyHasTimeDimension,
  ]);

  return {
    seriesData,
    seriesNameFormatter,
    xAxisOptions,
    yAxisOptions,
    tooltipOptions,
    seriesKey,
  };
};
